import * as React from 'react';
import TimeTaxSupportSystemClient from '../../system';
import { DateTime } from 'luxon';
import { DatePicker, Dropdown, IDropdownOption, PrimaryButton } from '@fluentui/react';
import { DateCustom } from '../../lib/DateCustom';
import MessageCourier from '../../lib/MessageCourier';
import { Company } from '../../system/company/Company';
import { useTranslation } from 'react-i18next';
import { getLocalizedStrings } from '../../lib/LocalizedStringsForDatePicker';
import { OrganizationChartDropdown } from '../Common/OrganizationChartDropdown';
import useAsyncDataCourier from '../../common/custom-hooks/useAsyncDataCourier';
import { User } from '../../system/User';
import CheckboxFilterComponent from '../../lib/CheckboxFilterComponent';
import useIsMobile from '../../common/custom-hooks/useIsMobile';

interface IReportingFilters {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  onChangeFromDate: (date: DateTime) => void;
  onChangeToDate: (date: DateTime) => void;
  onChangeCompany: (company?: Company | string) => void;
  onClickSearch: () => void;
  showCompanyFilter?: boolean;
  showSectorFilter?: boolean;
  useConcept?: boolean;
  onChangeConcept?: (concept: string) => void;
  onChangeNode?: (id: string) => void;
  user?: User;
}

interface CompanyOption {
  key: string;
  text: string;
  checked: boolean;
}

export const ReportingFilters = (props: IReportingFilters) => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = React.useState<DateTime | undefined>(undefined);
  const [toDate, setToDate] = React.useState<DateTime | undefined>(undefined);
  const [concept, setConcept] = React.useState<string>('');
  const [shouldRefresh, setShouldRefresh] = React.useState<boolean>(false);
  const [listCompanies, setListCompanies] = React.useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = React.useState<CompanyOption[]>([]);
  const { isMobile } = useIsMobile();

  const nodes = useAsyncDataCourier(
      () => {
        if (props.user) {
          return props.system.getOrganizationChartSystem().getAvailableNodesOf(props.user);
        }
        return Promise.resolve([]);
      },
      [props.system, props.user],
      props.courier
  );

  React.useEffect(() => {
    setFromDate(DateTime.fromJSDate(DateCustom.getFechaOneMonthAgo()));
    props.onChangeFromDate(DateTime.fromJSDate(DateCustom.getFechaOneMonthAgo()));
    setToDate(DateTime.fromJSDate(new Date()));
    props.onChangeToDate(DateTime.fromJSDate(new Date()));
    setConcept('empresa');
    if (props.onChangeConcept) props.onChangeConcept('empresa');
    getDataListCompany();
  }, []);

  const onChangeDate = (date: Date | null | undefined, name: string) => {
    if (date) {
      if (name === 'from') {
        setFromDate(DateTime.fromJSDate(date));
        props.onChangeFromDate(DateTime.fromJSDate(date));
        setShouldRefresh(!shouldRefresh);
        if (props.onChangeFromDate) props.onChangeFromDate(DateTime.fromJSDate(date));
      }
      if (name === 'to') {
        setToDate(DateTime.fromJSDate(date));
        props.onChangeToDate(DateTime.fromJSDate(date));
        setShouldRefresh(!shouldRefresh);
        if (props.onChangeToDate) props.onChangeToDate(DateTime.fromJSDate(date));
      }
    }
  };

  const getOptions = () => {
    let ret: IDropdownOption<any>[] = [
      { key: 'empresa', text: t('Empresas') },
      { key: 'proyecto', text: t('Proyectos') },
      { key: 'colaborador', text: t('Colaboradores') },
    ];
    return ret;
  };

  const getDataListCompany = async () => {
    const companySystem = props.system.getCompanySystem();
    const companies = await companySystem.getList();

    setListCompanies(companies);

    const newCompanies = companies.map((company) => ({
      key: company.getId(),
      text: company.getName(),
      checked: false,
    }));

    setSelectedCompany((prevList) => [...prevList, ...newCompanies]);
  }

  const handleApply = (selectedOptions) => {
    const checkedKeys = selectedOptions
      .filter((item) => item.checked)
      .map((item) => item.key);

    const filteredSecondArray = listCompanies.filter((item) => checkedKeys.includes(item.getId()));
    const idsString = filteredSecondArray.map((item) => item.getId()).join(",");

    if (filteredSecondArray && props.onChangeCompany) props.onChangeCompany(idsString);
  }

  return (
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row mt-12">
        {
            isMobile ? (
              <div className="ms-Grid-col ms-sm12 mt-1"  style={{display:"flex", flexDirection:"row", justifyContent:"space-between", gap:"2px"}}>
                <span className="ms-sm6">{t('Filtro')}</span>
                <PrimaryButton
              iconProps={{ iconName: 'Search' }}
              className="ms-sm6 ml-2"
              onClick={props.onClickSearch}
              text={t('Buscar')}
            />
              </div>
            ): null
          }
        {props.showCompanyFilter ? (
         <div className="ms-Grid-col ms-md3 ms-sm12 mt-1">
            <CheckboxFilterComponent
              title={t("Seleccionar empresas")}
              titleStyle = {{
                label:{
                  color:"dark"
                }
              }}
              initialOptions={selectedCompany}
              onApply={handleApply}
              dropdownStyle={{
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                borderRadius: "5px",
                position:"fixed"
              }}
            />
          </div>
          ) : null}
          {props.showSectorFilter && props.onChangeNode && nodes ? (
              <div className="ms-Grid-col ms-md3 ms-sm12 mt-1">
                <OrganizationChartDropdown
                    label={t('Sector')}
                    options={nodes}
                    useAllSector={true}
                    onSelectAll={() => {
                      if (props.onChangeNode) props.onChangeNode('');
                    }}
                    onChange={(node) => {
                      if (props.onChangeNode) props.onChangeNode(node.getId());
                    }}
                ></OrganizationChartDropdown>
              </div>
          ) : null}

          <div className="ms-Grid-col ms-md3 ms-sm6 mt-1">
            <DatePicker
                strings={getLocalizedStrings()}
                label={t('Filtrar desde')}
                formatDate={DateCustom.formatDate}
                value={fromDate ? fromDate.toJSDate() : undefined}
                onSelectDate={(e) => onChangeDate(e, 'from')}
            />
          </div>
          <div className="ms-Grid-col ms-md3 ms-sm6 mt-1">
            <DatePicker
                strings={getLocalizedStrings()}
                label={t('Filtrar hasta')}
                formatDate={DateCustom.formatDate}
                value={toDate ? toDate.toJSDate() : undefined}
                onSelectDate={(e) => onChangeDate(e, 'to')}
            />
          </div>
          {props.useConcept && (
              <div className="ms-Grid-col ms-md3 ms-sm6 mt-1">
                <Dropdown
                    label={t('Agrupar por concepto')}
                    onChange={(e, option) => {
                      if (props.onChangeConcept && option) {
                        props.onChangeConcept(String(option.key));
                        setConcept(String(option!.key));
                      }
                    }}
                    selectedKey={concept}
                    options={getOptions()}
                />
              </div>
          )}

          <div className="ms-Grid-col ms-sm2 mt-1" style={{ display: isMobile ? "none" : ""}}>
            <PrimaryButton
                iconProps={{ iconName: 'Search' }}
                className="mt-for-btn-search ml-2"
                onClick={props.onClickSearch}
                text={t('Buscar')}
            />
          </div>
        </div>
      </div>
  );
};
